import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Download as DownloadIcon, Loader2, ScrollText, Printer, Gift, Send } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { http, API, apiError } from "@/lib/api";

export default function Download() {
  const { token } = useParams();
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [posterName, setPosterName] = useState("");
  const [posterTagline, setPosterTagline] = useState("");
  const [posterSize, setPosterSize] = useState("8x10");
  const [gift, setGift] = useState({ to: "", toName: "", from: "", msg: "" });
  const [sendingGift, setSendingGift] = useState(false);

  const otherAssets = useMemo(
    () => (manifest ? manifest.assets.filter((a) => a.type !== "poster") : []),
    [manifest]
  );
  const hasPoster = useMemo(
    () => (manifest ? manifest.assets.some((a) => a.type === "poster") : false),
    [manifest]
  );

  useEffect(() => {
    http
      .get(`/download/${token}`)
      .then((r) => {
        setManifest(r.data);
        setPosterName(r.data.character?.name || "");
        setPosterTagline(r.data.default_tagline || "");
      })
      .catch((e) => setError(apiError(e, "This download link is invalid or expired.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function download(asset, params = {}) {
    setDownloading(asset.type);
    try {
      const res = await http.get(`/download/${token}/asset/${asset.type}`, {
        responseType: "blob",
        params,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${manifest.order_id}-${asset.type}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${asset.label} downloaded!`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(null);
    }
  }

  async function sendGift() {
    if (!/.+@.+\..+/.test(gift.to)) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    setSendingGift(true);
    try {
      const { data } = await http.post(`/download/${token}/gift`, {
        recipient_email: gift.to,
        recipient_name: gift.toName,
        sender_name: gift.from,
        message: gift.msg,
        origin_url: window.location.origin,
      });
      if (data.email_enabled) {
        toast.success(`Gift sent to ${gift.to}! 🎁`);
      } else {
        toast.success("Gift prepared! (Email is in demo mode — connect Resend to actually send it.)");
      }
      setGift({ to: "", toName: "", from: "", msg: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSendingGift(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <h1 className="font-display text-2xl font-bold text-[#4A235A]">Hmm, that link didn't work</h1>
          <p className="mt-2 text-sm font-medium text-[#5C4A61]" data-testid="download-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="grid min-h-screen place-items-center bg-parchment">
        <Loader2 className="animate-spin text-[#9B5DE5]" size={40} />
      </div>
    );
  }

  const c = manifest.character || {};
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8" data-testid="download-page">
        <span className="inline-flex rounded-full bg-[#4A235A]/8 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#4A235A]">
          {manifest.package_name} • Order {manifest.order_id}
        </span>
        <h1 className="mt-4 font-display text-3xl font-black text-[#1A0F1C] sm:text-4xl">
          {c.name || "Your legend"} is ready to download
        </h1>
        {manifest.expires && (
          <p className="mt-2 text-sm font-semibold text-[#5C4A61]">
            Available until {new Date(manifest.expires).toLocaleDateString()}
          </p>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {otherAssets.map((a) => (
              <button
                key={a.type}
                onClick={() => download(a)}
                disabled={downloading === a.type}
                data-testid={`download-asset-${a.type}`}
                className="flex min-h-[64px] items-center justify-between gap-3 rounded-2xl bg-[#F4D03F] px-6 font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-1 disabled:opacity-60"
              >
                <span>{a.label}</span>
                {downloading === a.type ? <Loader2 className="animate-spin" size={20} /> : <DownloadIcon size={20} />}
              </button>
            ))}
        </div>

        {hasPoster && (
          <div className="mt-6 rounded-3xl border border-[#4A235A]/10 bg-white p-6 shadow-[0_8px_32px_rgba(74,35,90,0.06)] sm:p-8" data-testid="poster-card">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-[#4A235A]">
              <Printer size={22} className="text-[#9B5DE5]" /> Print-ready poster
            </h2>
            <p className="mt-1 text-sm font-medium text-[#5C4A61]">
              Tweak the name and tagline, pick your frame size, and download a 300 DPI poster ready to print.
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-bold text-[#4A235A]">Name on poster</label>
                <input
                  value={posterName}
                  onChange={(e) => setPosterName(e.target.value)}
                  maxLength={60}
                  data-testid="poster-name-input"
                  className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-bold text-[#4A235A]">Tagline (optional)</label>
                <input
                  value={posterTagline}
                  onChange={(e) => setPosterTagline(e.target.value)}
                  maxLength={160}
                  data-testid="poster-tagline-input"
                  className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-sm font-bold text-[#4A235A]">Frame size</label>
              <div className="flex flex-wrap gap-3">
                {(manifest.poster_sizes || []).map((sz) => (
                  <button
                    key={sz.id}
                    onClick={() => setPosterSize(sz.id)}
                    data-testid={`poster-size-${sz.id}`}
                    className={`min-h-[48px] rounded-full border px-6 font-bold transition-colors ${
                      posterSize === sz.id
                        ? "border-[#4A235A] bg-[#4A235A] text-white"
                        : "border-[#4A235A]/20 text-[#4A235A] hover:bg-[#4A235A]/5"
                    }`}
                  >
                    {sz.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() =>
                download(
                  { type: "poster", label: "Poster" },
                  { name: posterName, tagline: posterTagline, size: posterSize }
                )
              }
              disabled={downloading === "poster"}
              data-testid="download-asset-poster"
              className="mt-6 inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-[#F4D03F] px-8 font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-1 disabled:opacity-60"
            >
              {downloading === "poster" ? <Loader2 className="animate-spin" size={20} /> : <Printer size={20} />}
              Download poster
            </button>
          </div>
        )}

        <div className="mt-6 rounded-3xl border border-[#4A235A]/10 bg-white p-6 shadow-[0_8px_32px_rgba(74,35,90,0.06)] sm:p-8" data-testid="gift-card">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-[#4A235A]">
            <Gift size={22} className="text-[#9B5DE5]" /> Send as a gift
          </h2>
          <p className="mt-1 text-sm font-medium text-[#5C4A61]">
            Email this legend straight to a grandparent, friend, or family member with a personal note.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <input
              type="email"
              value={gift.to}
              onChange={(e) => setGift({ ...gift, to: e.target.value })}
              placeholder="Recipient's email *"
              data-testid="gift-to-input"
              className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
            />
            <input
              value={gift.toName}
              onChange={(e) => setGift({ ...gift, toName: e.target.value })}
              placeholder="Recipient's name (optional)"
              data-testid="gift-toname-input"
              className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
            />
            <input
              value={gift.from}
              onChange={(e) => setGift({ ...gift, from: e.target.value })}
              placeholder="Your name (optional)"
              data-testid="gift-from-input"
              className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
            />
            <input
              value={gift.msg}
              onChange={(e) => setGift({ ...gift, msg: e.target.value })}
              placeholder="A short message (optional)"
              maxLength={160}
              data-testid="gift-message-input"
              className="w-full rounded-2xl border border-[#4A235A]/15 bg-white p-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
            />
          </div>
          <button
            onClick={sendGift}
            disabled={sendingGift}
            data-testid="gift-send-button"
            className="mt-6 inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-[#4A235A] px-8 font-extrabold text-white transition-transform hover:-translate-y-1 disabled:opacity-60"
          >
            {sendingGift ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />} Send this legend
          </button>
        </div>

        {manifest.story && (
          <div className="mt-10 rounded-3xl border border-[#4A235A]/10 bg-white p-6 shadow-[0_8px_32px_rgba(74,35,90,0.06)] sm:p-8">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-[#4A235A]">
              <ScrollText size={22} className="text-[#9B5DE5]" /> The legend of {c.name}
            </h2>
            {manifest.story.tagline && (
              <p className="mt-3 font-display text-xl font-bold italic text-[#9B5DE5]">“{manifest.story.tagline}”</p>
            )}
            {manifest.story.personality && (
              <p className="mt-4 text-sm font-medium leading-relaxed text-[#5C4A61]">
                <span className="font-bold text-[#4A235A]">Personality: </span>
                {manifest.story.personality}
              </p>
            )}
            {manifest.story.backstory && (
              <p className="mt-3 whitespace-pre-line text-sm font-medium leading-relaxed text-[#5C4A61]">
                {manifest.story.backstory}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
